import { useMemo } from "react";
import * as THREE from "three";
import { clone as skeletonClone } from 'three/examples/jsm/utils/SkeletonUtils.js';
import {
  OUTLINE_ENABLED,
  OUTLINE_COLOR,
  CHARACTER_OUTLINE_THICKNESS,
  CHARACTER_OUTLINE_OPACITY,
} from "../config/game";
import { computeSmoothedNormals } from "../utils/computeSmoothedNormals";

const skinnedOutlineMaterial = OUTLINE_ENABLED
  ? new THREE.ShaderMaterial({
      side: THREE.BackSide,
      transparent: true,
      uniforms: {
        outlineThickness: { value: CHARACTER_OUTLINE_THICKNESS },
        outlineColor: { value: OUTLINE_COLOR },
        outlineOpacity: { value: CHARACTER_OUTLINE_OPACITY },
      },
      vertexShader: `
        #include <common>
        #include <skinning_pars_vertex>

        attribute vec3 smoothNormal;
        uniform float outlineThickness;

        void main() {
          #include <skinbase_vertex>
          #include <beginnormal_vertex>
          objectNormal = smoothNormal;
          #include <skinnormal_vertex>
          #include <begin_vertex>
          #include <skinning_vertex>

          vec4 mvPosition = modelViewMatrix * vec4(transformed, 1.0);
          vec3 viewNormal = normalize(normalMatrix * objectNormal);
          float dist = -mvPosition.z;
          mvPosition.xyz += viewNormal * outlineThickness * dist * 0.001;
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform vec3 outlineColor;
        uniform float outlineOpacity;
        void main() {
          gl_FragColor = vec4(outlineColor, outlineOpacity);
        }
      `,
    })
  : null;

const staticOutlineMaterial = OUTLINE_ENABLED
  ? new THREE.ShaderMaterial({
      side: THREE.BackSide,
      transparent: true,
      uniforms: {
        outlineThickness: { value: CHARACTER_OUTLINE_THICKNESS },
        outlineColor: { value: OUTLINE_COLOR },
        outlineOpacity: { value: CHARACTER_OUTLINE_OPACITY },
      },
      vertexShader: `
        attribute vec3 smoothNormal;
        uniform float outlineThickness;
        void main() {
          vec4 viewPos = modelViewMatrix * vec4(position, 1.0);
          vec3 viewNormal = normalize(normalMatrix * smoothNormal);
          float dist = -viewPos.z;
          viewPos.xyz += viewNormal * outlineThickness * dist * 0.001;
          gl_Position = projectionMatrix * viewPos;
        }
      `,
      fragmentShader: `
        uniform vec3 outlineColor;
        uniform float outlineOpacity;
        void main() {
          gl_FragColor = vec4(outlineColor, outlineOpacity);
        }
      `,
    })
  : null;

export const usePersonModel = (gltf: any) => {
  const clonedScene = useMemo<THREE.Object3D | null>(() => {
    if (gltf?.scene) {
      let scene;
      try {
        scene = skeletonClone(gltf.scene);
      } catch {
        scene = gltf.scene.clone(true);
      }

      // Collect meshes first to avoid modifying scene graph during traversal
      const meshesToOutline: { mesh: any; parent: THREE.Object3D }[] = [];

      scene.traverse((child: any) => {
        if (child.isMesh) {
          child.castShadow = true;
          child.receiveShadow = true;
          if (OUTLINE_ENABLED && child.parent) {
            // Compute smoothed normals for cleaner outline
            if (!child.geometry.getAttribute("smoothNormal")) {
              computeSmoothedNormals(child.geometry);
            }
            meshesToOutline.push({ mesh: child, parent: child.parent });
          }
        }
      });

      // Add outline meshes as siblings
      for (const { mesh, parent } of meshesToOutline) {
        if (mesh.isSkinnedMesh) {
          const outlineMesh = new THREE.SkinnedMesh(mesh.geometry, skinnedOutlineMaterial!);
          outlineMesh.skeleton = mesh.skeleton;
          outlineMesh.bindMatrix.copy(mesh.bindMatrix);
          outlineMesh.bindMatrixInverse.copy(mesh.bindMatrixInverse);
          outlineMesh.renderOrder = 1;
          outlineMesh.frustumCulled = false;
          parent.add(outlineMesh);
        } else {
          const outlineMesh = new THREE.Mesh(mesh.geometry, staticOutlineMaterial!);
          outlineMesh.renderOrder = 1;
          outlineMesh.frustumCulled = false;
          outlineMesh.position.copy(mesh.position);
          outlineMesh.rotation.copy(mesh.rotation);
          outlineMesh.scale.copy(mesh.scale);
          parent.add(outlineMesh);
        }
      }

      return scene;
    }
    return null;
  }, [gltf]);

  const nodes = gltf?.nodes || {};
  const animations = gltf?.animations || [];

  return { clonedScene, nodes, animations };
};
